/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AliasType,
  ContactType,
  Entity,
  Provider,
  SubmissionDetailData,
  SubmissionInput,
  SubmissionType,
  getDisplayNameFromEntity,
  getPaginatedNodes,
} from '@converge/graphql';
import { toTitleCase } from '@converge/data';
import { IUIUnderwritingOverviewModel } from './underwriting.submission.detail.overview';

import {
  mapAddressToUIModel,
  mapInsuredEntityToFormData,
} from '../../entity/insured/overview/insured-entity.detail.overview';
import { convergeLogger } from '@converge/config';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { isNotNullOrUndefined, isObject } from '@converge/utils';

const toNumberOrNull = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);
export const mapToUIOverviewInsuredModel = (
  data: SubmissionDetailData,
  policyFundamentals?: any
): IUIUnderwritingOverviewModel => {
  const insuredEntity = mapInsuredEntityToFormData(
    data?.insuredEntity?.node as unknown as Entity,
    policyFundamentals
  );

  // revenue always comes from the insured entity's own fundamental, not the policy fundamental
  const insuredEntityPrimaryFundamentalNode =
    data?.insuredEntity?.node?.fundamentals?.edges?.find(
      (edge) => edge.node.primary
    )?.node;
  if (insuredEntityPrimaryFundamentalNode) {
    if (insuredEntity.primaryFundamentals) {
      insuredEntity.primaryFundamentals = {
        ...insuredEntity.primaryFundamentals,
        revenue: insuredEntityPrimaryFundamentalNode.revenue,
      };
    } else {
      insuredEntity.primaryFundamentals = insuredEntityPrimaryFundamentalNode;
    }
  }

  const product = {
    value: data?.product?.node.uuid,
    label: data?.product?.node.name,
  };

  const program = {
    value: data?.program?.node.uuid,
    label: data?.program?.node.name,
  };

  const partner = {
    value: data?.partner?.node.uuid,
    label: data?.partner && getDisplayNameFromEntity(data.partner.node),
  };

  const brokerContact =
    getPaginatedNodes(data.persons)?.find(
      (person) => person.type === 'agency_contact' && person.primary
    ) ??
    getPaginatedNodes(data.persons)?.find(
      (person) => person.type === 'agency_contact'
    ) ??
    getPaginatedNodes(data.persons)?.find(
      (person) => person.agent && person.primary
    ) ??
    getPaginatedNodes(data.brokerEntity?.node.persons)?.find(
      (person) => person.agent && person.primary
    ) ??
    getPaginatedNodes(data.brokerEntity?.node.persons)?.find(
      (person) => person.agent
    );

  const primaryBrokerAddress =
    getPaginatedNodes(data.brokerEntity?.node.addresses)?.find(
      (address) => address?.type === 'CORPORATE'
    ) ?? getPaginatedNodes(data.brokerEntity?.node.addresses)[0];

  const brokerEntity = {
    uuid: data.brokerEntity?.node.uuid,
    primaryAlias: getPaginatedNodes(data.brokerEntity?.node.aliases).find(
      (alias) => alias.primary && alias.type === AliasType.LegalName
    ),
    primaryAddress: mapAddressToUIModel(primaryBrokerAddress),
    primaryContact: {
      uuid: brokerContact?.uuid,
      firstName: brokerContact?.firstName,
      lastName: brokerContact?.lastName,
      email:
        getPaginatedNodes(brokerContact?.contacts)?.find(
          (contact) => contact.type === ContactType.Email && contact.primary
        )?.value ??
        getPaginatedNodes(brokerContact?.contacts)?.find(
          (contact) => contact.type === ContactType.Email
        )?.value,
      phone:
        getPaginatedNodes(brokerContact?.contacts)?.find(
          (contact) => contact.type === ContactType.Phone && contact.primary
        )?.value ??
        getPaginatedNodes(brokerContact?.contacts)?.find(
          (contact) => contact.type === ContactType.Phone
        )?.value,
    },
  };

  const defaultValues: IUIUnderwritingOverviewModel = {
    uuid: data.uuid,
    status: data.status,
    brokerCommission: data.brokerCommission
      ? data.brokerCommission * 100
      : data.brokerCommission,
    aggregateLimit: data.aggregateLimit,
    retention: data.policyRetention,
    type: toTitleCase(data.type ?? ''),
    programUuid: data?.program?.node?.uuid,
    policyFundamentals: policyFundamentals,
    createdDate: data?.receivedAt,
    effectiveDate: data?.policyStartDate,
    expirationDate: data?.policyEndDate,
    notes: data.notes,
    insuredEntity: insuredEntity,
    brokerEntity: brokerEntity,
    policyFee: data.policyFee,
    product,
    partner,
    program,
    prosAndCons: data.prosAndCons ?? '',
    descriptionOfOperations: data.descriptionOfOperations ?? '',
    financialAnalysisSummary: data.financialAnalysisSummary ?? '',
    surplusLinesTaxLicenseUuid: data.surplusLinesTaxLicenseUuid ?? '',
    propertyAndCasualtyLicenseUuid: data.propertyAndCasualtyLicenseUuid ?? '',
  };

  convergeLogger.debug('defaultValues', defaultValues);

  return defaultValues;
};

export const mapToAPIOverviewModel = (
  uiData: IUIUnderwritingOverviewModel,
  dirtyFields: FieldNamesMarkedBoolean<IUIUnderwritingOverviewModel>
): SubmissionInput => {
  const submissionInput: SubmissionInput = {
    uuid: uiData.uuid,
  };

  convergeLogger.debug({ dirtyFields, uiData });

  if (
    'brokerEntity' in dirtyFields &&
    isNotNullOrUndefined(dirtyFields.brokerEntity)
  ) {
    if (
      'primaryContact' in dirtyFields.brokerEntity &&
      isObject(dirtyFields.brokerEntity.primaryContact) &&
      dirtyFields.brokerEntity.primaryContact.uuid
    ) {
      submissionInput.brokerAgentUuid =
        uiData.brokerEntity?.primaryContact?.uuid;
      submissionInput.brokerUuid = uiData?.brokerEntity?.uuid;
    } else {
      submissionInput.brokerUuid = uiData?.brokerEntity?.uuid;
    }
  }

  if (
    'insuredEntity' in dirtyFields &&
    isNotNullOrUndefined(dirtyFields.insuredEntity)
  ) {
    if (
      'primaryContact' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryContact) &&
      (dirtyFields.insuredEntity.primaryContact.firstName ||
        dirtyFields.insuredEntity.primaryContact.lastName ||
        dirtyFields.insuredEntity.primaryContact.email ||
        dirtyFields.insuredEntity.primaryContact.phone)
    ) {
      if (dirtyFields.insuredEntity.primaryContact.firstName) {
        submissionInput.contactFirstName =
          uiData.insuredEntity?.primaryContact?.firstName;
      }
      if (dirtyFields.insuredEntity.primaryContact.lastName) {
        submissionInput.contactLastName =
          uiData.insuredEntity?.primaryContact?.lastName;
      }
      if (dirtyFields.insuredEntity.primaryContact.email) {
        submissionInput.contactEmailAddress =
          uiData.insuredEntity?.primaryContact?.email;
      }
      if (dirtyFields.insuredEntity.primaryContact.phone) {
        submissionInput.contactPhoneNumber =
          uiData.insuredEntity?.primaryContact?.phone;
      }
    }

    if (
      'primaryLegalAlias' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryLegalAlias) &&
      dirtyFields.insuredEntity.primaryLegalAlias.name
    ) {
      submissionInput.primaryAlias = [
        {
          entityUuid: uiData.insuredEntity?.uuid,
          aliasUuid: uiData.insuredEntity?.primaryLegalAlias?.uuid,
          source: Provider.Primary,
          type: AliasType.LegalName,
          name: uiData.insuredEntity?.primaryLegalAlias?.name,
        },
      ];
    }

    if (
      'primaryTradingName' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryTradingName) &&
      dirtyFields.insuredEntity.primaryTradingName.name
    ) {
      submissionInput.primaryAlias = submissionInput.primaryAlias || [];
      submissionInput.primaryAlias.push({
        entityUuid: uiData.insuredEntity?.uuid,
        aliasUuid: uiData.insuredEntity?.primaryTradingName?.uuid,
        source: Provider.Primary,
        type: AliasType.TradingName,
        name: uiData.insuredEntity?.primaryTradingName?.name,
      });
    }

    if (
      'primaryAddress' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryAddress) &&
      (dirtyFields.insuredEntity.primaryAddress.street1 ||
        dirtyFields.insuredEntity.primaryAddress.street2 ||
        dirtyFields.insuredEntity.primaryAddress.city ||
        dirtyFields.insuredEntity.primaryAddress.region ||
        dirtyFields.insuredEntity.primaryAddress.country ||
        dirtyFields.insuredEntity.primaryAddress.postalCode)
    ) {
      submissionInput.primaryAddress = {
        entityUuid: uiData.insuredEntity?.uuid,
        addressUuid: uiData.insuredEntity?.primaryAddress?.uuid,
        source: Provider.Primary,
        street1: uiData.insuredEntity?.primaryAddress?.street1,
        street2: uiData.insuredEntity?.primaryAddress?.street2,
        city: uiData.insuredEntity?.primaryAddress?.city,
        regionUuid: uiData.insuredEntity?.primaryAddress?.region,
        countryUuid: uiData.insuredEntity?.primaryAddress?.country,
        postalCode: uiData.insuredEntity?.primaryAddress?.postalCode,
      };
    }

    if (
      'primaryIndustrySector' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryIndustrySector) &&
      dirtyFields.insuredEntity.primaryIndustrySector.naicsCode
    ) {
      submissionInput.primaryIndustrySector = {
        entityUuid: uiData.insuredEntity?.uuid,
        industrySectorUuid: uiData.insuredEntity?.primaryIndustrySector?.uuid,
        source: Provider.Primary,
        naicsCode: uiData.insuredEntity?.primaryIndustrySector?.naicsCode,
      };
    }

    if (
      'primaryFundamentals' in dirtyFields.insuredEntity &&
      isObject(dirtyFields.insuredEntity.primaryFundamentals) &&
      (dirtyFields.insuredEntity.primaryFundamentals.revenue ||
        dirtyFields.insuredEntity.primaryFundamentals.employees ||
        dirtyFields.insuredEntity.primaryFundamentals.projectedRevenue ||
        dirtyFields.insuredEntity.primaryFundamentals.cashRatio ||
        dirtyFields.insuredEntity.primaryFundamentals.quickRatio ||
        dirtyFields.insuredEntity.primaryFundamentals.currentRatio ||
        dirtyFields.insuredEntity.primaryFundamentals.foreignRevenue ||
        dirtyFields.insuredEntity.primaryFundamentals.projectedForeignRevenue ||
        dirtyFields.insuredEntity.primaryFundamentals.ratedRevenueOverride)
    ) {
      submissionInput.primaryFundamental = {
        entityUuid: uiData.insuredEntity?.uuid,
        fundamentalUuid: uiData.insuredEntity?.primaryFundamentals?.uuid,
        source: Provider.Primary,
        revenue: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.revenue
        ),
        employees: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.employees
        ),
        projectedRevenue: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.projectedRevenue
        ),
        currentRatio: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.currentRatio
        ),
        quickRatio: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.quickRatio
        ),
        cashRatio: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.cashRatio
        ),
        foreignRevenue: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.foreignRevenue
        ),
        projectedForeignRevenue: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.projectedForeignRevenue
        ),
        ratedRevenueOverride: toNumberOrNull(
          uiData.insuredEntity?.primaryFundamentals?.ratedRevenueOverride
        ),
      };
    }
  }

  if (dirtyFields.status) {
    submissionInput.status = uiData.status;
  }
  if (dirtyFields.propertyAndCasualtyLicenseUuid) {
    submissionInput.propertyAndCasualtyLicenseUuid =
      uiData.propertyAndCasualtyLicenseUuid;
  }
  if (dirtyFields.surplusLinesTaxLicenseUuid) {
    submissionInput.surplusLinesTaxLicenseUuid =
      uiData.surplusLinesTaxLicenseUuid;
  }

  if (dirtyFields.notes) {
    submissionInput.notes = uiData.notes;
  }
  if (dirtyFields.prosAndCons) {
    submissionInput.prosAndCons = uiData.prosAndCons;
  }
  if (dirtyFields.descriptionOfOperations) {
    submissionInput.descriptionOfOperations = uiData.descriptionOfOperations;
  }
  if (dirtyFields.financialAnalysisSummary) {
    submissionInput.financialAnalysisSummary = uiData.financialAnalysisSummary;
  }

  if (dirtyFields.policyFee) {
    submissionInput.policyFee = Number(uiData.policyFee);
  }

  if (dirtyFields.program) {
    submissionInput.programUuid = uiData.program.value;
  }

  if (dirtyFields.partner) {
    // Don't send partnerUuid if Digital program is selected
    if (uiData.program.label !== 'Digital') {
      submissionInput.partnerUuid =
        uiData.partner.value === 'no partner' ? 'null' : uiData.partner.value;
    }
  }
  if (dirtyFields.product) {
    submissionInput.productUuid = uiData.product.value;
  }

  if (dirtyFields.type) {
    submissionInput.type =
      SubmissionType[uiData.type as keyof typeof SubmissionType];
  }

  if (dirtyFields.brokerCommission) {
    submissionInput.brokerCommission =
      typeof uiData.brokerCommission === 'string' ||
      typeof uiData.brokerCommission === 'number'
        ? Number(uiData.brokerCommission) / 100 || 0
        : 0;
  }

  if (dirtyFields.aggregateLimit) {
    submissionInput.aggregateLimit = Number(uiData.aggregateLimit);
  }

  if (dirtyFields.retention) {
    submissionInput.policyRetention = Number(uiData.retention);
  }

  if (dirtyFields.effectiveDate) {
    submissionInput.policyStartDate = uiData.effectiveDate;
  }

  if (dirtyFields.expirationDate) {
    submissionInput.policyEndDate = uiData.expirationDate;
  }

  if (dirtyFields.createdDate) {
    submissionInput.receivedAt = uiData.createdDate;
  }

  return submissionInput;
};
